import { useCallback, useEffect, useId, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useTranslation } from '../../../i18n/useTranslation';
import { Dialog } from '../../ui/Dialog/Dialog';
import { Alert } from '../../atoms/Alert/Alert';
import { Button } from '../../atoms/Button/Button';
import { Heading } from '../../atoms/Typography/Heading';
import { Text } from '../../atoms/Typography/Text';
import { Stack } from '../../atoms/Stack/Stack';
import { ErrorBoundary } from '../../errors/ErrorBoundary';
import { ApiError } from '../../../lib/apiClient';
import { formatLegalDate } from '../../../api/legal';
import {
  PriorityNoticeContext,
  usePriorityNoticeActive,
  type PriorityNoticeState,
} from '../../../contexts/PriorityNoticeContext';
import {
  ACCOUNT_SETTINGS_PATH,
  clearTermsAcceptanceRequired,
  getTermsState,
  markTermsAcceptanceRequired,
  subscribeTermsState,
} from '../../../lib/termsStatus';
import {
  acceptTermsOfService,
  declineTermsOfService,
  fetchTermsOfServiceStatus,
  type TermsOfServiceStatus,
} from '../../../services/legalStatusService';
import '../PrivacyPolicyNotice/PrivacyPolicyNotice.css';
import './TermsOfServiceConsent.css';

/**
 * Per-tab memory of a dialog closed without answering (× / Escape): it does not
 * open by itself again in this session, but at the next sign-in (sign-out
 * clears sessionStorage, lib/userSession). The read-only notice can reopen it.
 */
export const TERMS_DIALOG_DISMISSED_KEY = 'reefguide-terms-dialog-dismissed';
/** The full terms of service (App.tsx: /agb and /terms render the same page in the UI language). */
export const TERMS_PATH = '/agb';
/** Give up waiting for the status after this long — never hold other dialogs back for ever. */
const STATUS_TIMEOUT_MS = 10_000;

function readDismissed(): string | null {
  try {
    return sessionStorage.getItem(TERMS_DIALOG_DISMISSED_KEY);
  } catch {
    return null;
  }
}

function writeDismissed(version: string | null): void {
  try {
    if (version === null) sessionStorage.removeItem(TERMS_DIALOG_DISMISSED_KEY);
    else sessionStorage.setItem(TERMS_DIALOG_DISMISSED_KEY, version);
  } catch {
    // storage unavailable — the in-memory state still closes the dialog for this page load
  }
}

/** End-to-end suites drive automated browsers with long-lived test accounts; never block them with a modal. */
function isAutomatedBrowser(): boolean {
  try {
    return typeof navigator !== 'undefined' && navigator.webdriver === true;
  } catch {
    return false;
  }
}

/** idle → checking → open | done. `open` shows the question or, after "Decline", the declined view. */
type Phase = 'idle' | 'checking' | 'open' | 'done';
type View = 'question' | 'declined';

interface OptionalAuthState {
  isAuthenticated?: boolean;
  isLoading?: boolean;
  user?: { email?: string } | null;
}

/** The auth state, or null without an AuthProvider (see PrivacyPolicyNotice). */
function useOptionalAuthState(): OptionalAuthState | null {
  try {
    return (useAuth() as { state?: OptionalAuthState } | null)?.state ?? null;
  } catch {
    return null;
  }
}

function CloseOnCrash({ onCrash }: { onCrash: () => void }) {
  useEffect(() => {
    onCrash();
  }, [onCrash]);
  return null;
}

interface TermsDialogProps {
  status: TermsOfServiceStatus;
  view: View;
  saving: 'acceptance' | 'decline' | null;
  saveFailed: boolean;
  onAccept: () => void;
  onDecline: () => void;
  onClose: () => void;
}

function TermsOfServiceDialog({ status, view, saving, saveFailed, onAccept, onDecline, onClose }: TermsDialogProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language ?? 'en';
  const date = (iso: string): string => formatLegalDate(iso, locale);
  const changesTitleId = useId();
  const changes = status.changes;
  const busy = saving !== null;

  if (view === 'declined') {
    return (
      <Dialog
        isOpen
        onClose={onClose}
        title={t('legal.termsConsent.declinedTitle')}
        size="md"
        data-testid="terms-of-service-declined"
        footer={
          <div className="rg-terms-consent__footer">
            <Button variant="secondary" onClick={onClose}>
              {t('legal.termsConsent.close')}
            </Button>
            <Button variant="primary" onClick={onAccept} isLoading={saving === 'acceptance'} disabled={busy}>
              {t('legal.termsConsent.accept')}
            </Button>
          </div>
        }
      >
        <Stack gap="4">
          <Text>{t('legal.termsConsent.declinedIntro', { version: status.currentVersion })}</Text>
          <Text>{t('legal.termsConsent.declinedOptions')}</Text>
          <ul className="rg-terms-consent__options">
            <li>
              <a className="rg-privacy-notice__link" href={ACCOUNT_SETTINGS_PATH}>
                {t('legal.termsConsent.exportData')}
              </a>
            </li>
            <li>
              <a className="rg-privacy-notice__link" href={ACCOUNT_SETTINGS_PATH}>
                {t('legal.termsConsent.deleteAccount')}
              </a>
            </li>
          </ul>
          {saveFailed && (
            <Alert tone="danger" compact>
              {t('legal.termsConsent.saveFailed')}
            </Alert>
          )}
        </Stack>
      </Dialog>
    );
  }

  return (
    <Dialog
      isOpen
      onClose={onClose}
      title={t('legal.termsConsent.title')}
      size="md"
      data-testid="terms-of-service-consent"
      footer={
        <div className="rg-terms-consent__footer">
          <Button variant="secondary" onClick={onDecline} isLoading={saving === 'decline'} disabled={busy}>
            {t('legal.termsConsent.decline')}
          </Button>
          <Button variant="primary" onClick={onAccept} isLoading={saving === 'acceptance'} disabled={busy}>
            {t('legal.termsConsent.accept')}
          </Button>
        </div>
      }
    >
      <Stack gap="4">
        <Text>{t('legal.termsConsent.intro')}</Text>
        <Text variant="muted" data-testid="terms-of-service-effective">
          {t('legal.updateNotice.effective', { date: date(status.effectiveDate), version: status.currentVersion })}
        </Text>
        {changes.length > 0 && (
          <section className="rg-privacy-notice__changes" aria-labelledby={changesTitleId}>
            <Heading level={3} id={changesTitleId} className="rg-privacy-notice__changes-title">
              {t('legal.updateNotice.changesTitle')}
            </Heading>
            <ul className="rg-privacy-notice__list">
              {changes.map((change) => (
                <li key={change.version} className="rg-privacy-notice__item">
                  {changes.length > 1 && (
                    <Text as="span" variant="label" className="rg-privacy-notice__version">
                      {t('legal.updateNotice.changeVersion', { version: change.version, date: date(change.effectiveDate) })}
                    </Text>
                  )}
                  <Text as="span">{change.summary}</Text>
                </li>
              ))}
            </ul>
          </section>
        )}
        <Text>
          <a className="rg-privacy-notice__link" href={TERMS_PATH} target="_blank" rel="noopener noreferrer">
            {t('legal.termsConsent.readTerms')} {t('legal.opensInNewTab')}
          </a>
        </Text>
        <Text variant="muted">
          {t(status.restricted ? 'legal.termsConsent.consentNoteReadOnly' : 'legal.termsConsent.consentNote')}
        </Text>
        {saveFailed && (
          <Alert tone="danger" compact>
            {t('legal.termsConsent.saveFailed')}
          </Alert>
        )}
      </Stack>
    </Dialog>
  );
}

/**
 * Terms of service re-acceptance — a priority notice like PrivacyPolicyNotice.
 *
 * Mounted inside PrivacyPolicyNotice (App.tsx) and waits until that notice is
 * done, so the two dialogs never stack. After sign-in it asks GET
 * /api/v1/me/legal-status; when the terms in force are newer than the version
 * the user accepted it opens a modal Dialog with the change summary, a link to
 * the full terms (/agb) and two explicit buttons — "Ich akzeptiere die neuen
 * Nutzungsbedingungen" and "Ablehnen" (no checkbox, nothing pre-ticked).
 * Changed terms bind an existing customer only through such an acceptance
 * (BGH XI ZR 26/20); until then the account is read-only (backend
 * middleware/requireTermsAcceptance.ts) and TermsReadOnlyNotice explains it.
 *
 *  - Accept → POST …/terms-of-service/acceptance, read-only ends at once.
 *  - Decline → POST …/terms-of-service/decline, the dialog shows how to export
 *    the data or delete the account; it does not open by itself again (the
 *    read-only notice can reopen it).
 *  - × / Escape → nothing recorded; it opens again at the next sign-in.
 *
 * Provides PriorityNoticeContext to its children (active while its parent
 * notice or this dialog is being checked or open).
 */
export function TermsOfServiceConsent({ children }: { children?: ReactNode }) {
  const authState = useOptionalAuthState();
  const parentActive = usePriorityNoticeActive();
  const { i18n } = useTranslation();
  const signedIn = authState?.isAuthenticated === true;
  const ready = signedIn && authState?.isLoading !== true && !parentActive;
  const userKey = authState?.user?.email ?? '';
  const languageRef = useRef(i18n.resolvedLanguage ?? i18n.language ?? 'en');
  languageRef.current = i18n.resolvedLanguage ?? i18n.language ?? 'en';
  const { dialogRequests } = useSyncExternalStore(subscribeTermsState, getTermsState, getTermsState);
  const handledRequests = useRef(dialogRequests);

  const [phase, setPhase] = useState<Phase>('idle');
  const [view, setView] = useState<View>('question');
  const [status, setStatus] = useState<TermsOfServiceStatus | null>(null);
  const [saving, setSaving] = useState<'acceptance' | 'decline' | null>(null);
  const [saveFailed, setSaveFailed] = useState(false);

  useEffect(() => {
    if (signedIn) return;
    writeDismissed(null);
    setPhase('idle');
    setView('question');
    setStatus(null);
    setSaving(null);
    setSaveFailed(false);
  }, [signedIn]);

  useEffect(() => {
    if (!ready) return undefined;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), STATUS_TIMEOUT_MS);
    let settled = false;
    const finish = (next: TermsOfServiceStatus | null): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      setStatus(next);
      if (next?.restricted) markTermsAcceptanceRequired();
      const autoOpen =
        next !== null &&
        next.needsAcceptance &&
        next.declinedAt === null &&
        readDismissed() !== next.currentVersion &&
        !isAutomatedBrowser();
      setView('question');
      setSaveFailed(false);
      setPhase(autoOpen ? 'open' : 'done');
    };
    setPhase('checking');
    fetchTermsOfServiceStatus(languageRef.current, controller.signal)
      .then(finish)
      .catch(() => finish(null));
    controller.signal.addEventListener('abort', () => finish(null));
    return () => {
      settled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [ready, userKey]);

  // "Review and accept" in the read-only notice: open the dialog again.
  useEffect(() => {
    if (dialogRequests === handledRequests.current) return;
    handledRequests.current = dialogRequests;
    if (!signedIn || status === null || !status.needsAcceptance) return;
    setView('question');
    setSaveFailed(false);
    setPhase('open');
  }, [dialogRequests, signedIn, status]);

  const close = useCallback(() => {
    if (saving) return;
    if (view === 'question') writeDismissed(status?.currentVersion ?? null);
    setPhase('done');
  }, [saving, status, view]);

  const accept = useCallback(() => {
    if (!status || saving) return;
    setSaving('acceptance');
    setSaveFailed(false);
    acceptTermsOfService(status.currentVersion)
      .then(() => {
        clearTermsAcceptanceRequired();
        setStatus({ ...status, needsAcceptance: false, restricted: false, acceptedVersion: status.currentVersion });
        setPhase('done');
      })
      .catch((err: unknown) => {
        // 409: a newer version was published meanwhile — the next sign-in asks for that one.
        if (err instanceof ApiError && err.status === 409) setPhase('done');
        else setSaveFailed(true);
      })
      .finally(() => setSaving(null));
  }, [status, saving]);

  const decline = useCallback(() => {
    if (!status || saving) return;
    setSaving('decline');
    setSaveFailed(false);
    declineTermsOfService(status.currentVersion)
      .then(() => {
        setStatus({ ...status, declinedAt: new Date().toISOString() });
        setView('declined');
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 409) setPhase('done');
        else setSaveFailed(true);
      })
      .finally(() => setSaving(null));
  }, [status, saving]);

  const closeAfterCrash = useCallback(() => setPhase('done'), []);

  const value = useMemo<PriorityNoticeState>(
    () => ({ active: parentActive || (signedIn && (phase === 'checking' || phase === 'open')) }),
    [parentActive, signedIn, phase],
  );

  return (
    <PriorityNoticeContext.Provider value={value}>
      {children}
      {signedIn && phase === 'open' && status !== null && (
        <ErrorBoundary fallback={() => <CloseOnCrash onCrash={closeAfterCrash} />}>
          <TermsOfServiceDialog
            status={status}
            view={view}
            saving={saving}
            saveFailed={saveFailed}
            onAccept={accept}
            onDecline={decline}
            onClose={close}
          />
        </ErrorBoundary>
      )}
    </PriorityNoticeContext.Provider>
  );
}

export default TermsOfServiceConsent;
